// Compliance dashboard (/office/compliance). Two views on one page:
//  1. The CONSOLIDATED compliance dashboard (Session X Item 4): every statutory/management
//     compliance signal (LOLER, SAFed, engineer certs, competence reviews, RIDDOR advisory flags,
//     modification re-examination flags) brought together with a single health traffic light, from
//     one efficient GET /api/compliance/dashboard call. Manual refresh + a "last updated" stamp.
//  2. The operational tiles (LOLER/PPM/defects/renewals) from GET /api/compliance/overview, the
//     Master-Sheet mirror -- each red/amber tile carrying its drill-in list.
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, ApiError } from "../../lib/api";
import { MODAL_ACTION_BTN, MODAL_ACTIONS, ModalShell } from "../../components/ModalShell";
import type {
  ComplianceOverview,
  ComplianceTile,
  ComplianceDashboard,
  ComplianceHealthStatus,
  LolerChaseItem,
  SubcontractorExpiringCertificate,
  SubCertStatus,
  AccidentDashboardSummary,
  EnforcementActionDashboardSummary,
} from "../../lib/api";
import { EVENT_TYPE_LABEL, writtenReportCountdown } from "./accidentsUi";
import { Button } from "../../components/Button";
import { Loader } from "../../components/Loader";
import { PageHeader } from "../../components/PageHeader";
import { SafedAttentionSection } from "./SafedAttentionSection";
import { formatDate, formatTime } from "../../lib/format";

type Tone = "neutral" | "green" | "amber" | "red";

const BAND: Record<Tone, string> = {
  neutral: "bg-ele-inset-strong text-ele-strong",
  // Dark text on the green band per the platform convention (white on green is the retired look).
  green: "bg-ele-green text-ele-bg",
  amber: "bg-amber-400 text-black",
  // The fixed #c0392b fill with white ink (5.44:1 on both themes), never a palette red.
  red: "bg-ele-danger-fill text-white",
};

// A tile row's date, or "No date" where the record holds none.
const orNoDate = (iso: string | null) => (iso ? formatDate(iso) : "No date");

// For the LOLER/PPM operational tiles, ComplianceDetailItem.assetId is the lift id, so each row
// links straight to that lift's detail page (where the office acts on the overdue/due service or
// LOLER). assetId is explicit (never the defect/contract id), with id as a defensive fallback.
function liftHref(item: ComplianceTile["items"][number]): string {
  return `/office/lifts/${item.assetId ?? item.id}`;
}

export function CompliancePage() {
  return (
    <div>
      <PageHeader
        className="mb-5"
        title="Compliance"
        subtitle="Portfolio compliance at a glance. The health panel brings every statutory signal together; the operational tiles below mirror the service schedule. Regulatory advisories are guidance for review, not legal advice."
      />

      <ComplianceAreas />

      <HealthDashboard />

      <EquipmentChaseList />

      <h2 className="mb-3 mt-8 font-heading text-xl tracking-wide text-ele-blue">Operational</h2>
      <OperationalTiles />
    </div>
  );
}

// ---- Compliance areas (Session ATB, Phase 4, D30, M-2; two more added Session ATG) -----------
// Before Session ATB, LOLER, Complaints, Accidents and incidents, Enforcement notices and
// prosecutions, Environmental records, Document library, Internal audits, Management reviews and
// Compliance reports were nine sibling sidebar items with no shared home -- to the office, filing a
// record, filing a document and building an export are one errand, but the nav gave them nine
// separate doors. This grid is that one door: a card per area, linking straight through to its own
// unchanged page (none of these routes moved). It is navigation only, no new data and no changed
// business logic, matching the audit's own scope for the compliance hub gap (M-2).
//
// Engineer competence and Subcontractor register (Session ATG) were named in the original Phase 4
// scope but were left off ATB's nine-card grid; both already exist as working content elsewhere on
// this page (the "Competence reviews due" DashPanel, inline in HealthDashboard below) and on
// /office/subcontractors (SubcontractorCertPanel's rows already link into it), so these two cards
// are pure navigation to real, already-working destinations, never a new page.
const COMPLIANCE_AREAS: { to: string; title: string; description: string }[] = [
  { to: "/office/loler", title: "LOLER reports", description: "Statutory thorough-examination reports and the remedial work they raise." },
  { to: "/office/complaints", title: "Complaints", description: "Customer complaints, investigated and closed." },
  {
    to: "/office/accidents",
    title: "Accidents and incidents",
    description: "Workplace accidents, injuries and near misses, their RIDDOR assessment and the investigation. What CHAS asks for.",
  },
  {
    to: "/office/enforcement-actions",
    title: "Enforcement notices and prosecutions",
    description: "HSE / Local Authority enforcement notices and prosecutions, and the remedial action taken. What CHAS Q94 asks for.",
  },
  { to: "/office/environmental-records", title: "Environmental records", description: "Waste transfer and consignment notes, and the ISO 14001 environmental objectives." },
  { to: "/office/compliance/documents", title: "Document library", description: "ELE's own policies and procedures: the ISO 9001 / 14001 / 45001 management system files." },
  { to: "/office/compliance/audits", title: "Internal audits", description: "The ISO internal audit schedule and findings." },
  { to: "/office/compliance/management-reviews", title: "Management reviews", description: "The periodic ISO management review record." },
  { to: "/office/compliance-reports", title: "Compliance reports", description: "Build the compliance packs ELE sends out for accreditation and client procurement." },
  {
    to: "/office/personnel?tab=engineer",
    title: "Engineer competence",
    description: "Engineer certifications and competence reviews, due dates and expiries. See the Competence reviews due panel below for what needs attention now.",
  },
  {
    to: "/office/subcontractors",
    title: "Subcontractor register",
    description: "Approved subcontractors, their insurance, certificates and agreements. See the certificate expiry panel below for what needs attention now.",
  },
];

function ComplianceAreas() {
  return (
    <section className="mb-8">
      <h2 className="mb-3 font-heading text-xl tracking-wide text-ele-blue">Compliance areas</h2>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {COMPLIANCE_AREAS.map((area) => (
          <Link
            key={area.to}
            to={area.to}
            className="block rounded-ele border border-ele-purple-accent bg-ele-surface px-4 py-3 transition hover:border-ele-green"
          >
            <span className="font-heading text-ele-strong">{area.title}</span>
            <p className="mt-1 text-xs text-ele-body">{area.description}</p>
          </Link>
        ))}
      </div>
    </section>
  );
}

// ---- The LOLER paperwork chase list (Session ACR) ----------------------------
// Every people-lifting care-equipment asset with UNKNOWN examination status. THE LOLER
// CLOCK IS NOT OURS: no due date is fabricated for these assets and they are never in the
// reminder pipeline -- this list is their visibility, so the office chases the property
// manager for the paperwork (or records a known examination directly).
function EquipmentChaseList() {
  const [items, setItems] = useState<LolerChaseItem[] | null>(null);
  const [error, setError] = useState(false);
  const [recordFor, setRecordFor] = useState<LolerChaseItem | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(() => {
    setError(false);
    api.compliance
      .lolerChase()
      .then((d) => setItems(d.items))
      .catch(() => setError(true));
  }, []);
  useEffect(() => load(), [load]);

  const count = items?.length ?? 0;
  return (
    <section className="mt-8">
      <h2 className="mb-1 font-heading text-xl tracking-wide text-ele-blue">Equipment examination chase list</h2>
      <p className="mb-3 text-sm text-ele-body">
        People-lifting care equipment with no examination on record. The LOLER clock belongs to the examining
        body, so nothing is guessed: chase the property manager for the paperwork, then record the examination.
      </p>
      {notice && (
        <p role="status" className="mb-3 rounded-ele border border-ele-green bg-ele-surface px-3 py-2 text-sm text-ele-strong">
          {notice}
        </p>
      )}
      {error ? (
        <p role="alert" className="rounded-ele border border-ele-purple-accent bg-ele-surface p-4 text-center text-ele-strong">
          Could not load the chase list. Please try again in a moment.
        </p>
      ) : items === null ? (
        <Loader shape="inline" />
      ) : (
        <div className="overflow-hidden rounded-ele border border-ele-purple-accent">
          <div className={`flex items-baseline justify-between px-4 py-3 ${count > 0 ? BAND.amber : BAND.green}`}>
            <span className="font-menu text-xs uppercase tracking-widest">Awaiting first examination record</span>
            <span className="font-heading text-3xl">{count}</span>
          </div>
          {count === 0 ? (
            <p className="px-4 py-3 text-xs text-ele-body opacity-70">
              Every people-lifting equipment asset has an examination on record.
            </p>
          ) : (
            <ul className="divide-y divide-ele-purple-accent text-sm">
              {items.map((it) => (
                <li key={it.assetId} className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5">
                  <div>
                    <span className="font-heading text-ele-strong">
                      {[it.name, it.eleNumber].filter(Boolean).join(" | ") || "Equipment"}
                    </span>
                    <div className="text-xs text-ele-body">
                      {[it.siteName, it.customerName].filter(Boolean).join(" · ")}
                      {it.contractRef ? ` · Contract ${it.contractRef}` : ""}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-amber-400">Awaiting first examination record</span>
                    <Button variant="secondary" size="sm" onClick={() => setRecordFor(it)}>
                      Record examination
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {recordFor && (
        <RecordExamModal
          item={recordFor}
          onClose={() => setRecordFor(null)}
          onDone={(ref) => {
            setRecordFor(null);
            setNotice(`Examination recorded for ${ref}. The next due date now runs from the examination date.`);
            load();
          }}
        />
      )}
    </section>
  );
}

// Record a known examination for a chased equipment asset: the EXAM date (the clock rolls
// 6 months from it, never from today or the paperwork's arrival), an optional report
// reference and the examining body. Creates a LOLER report on the asset; correcting or
// deleting that report later recomputes the due date.
function RecordExamModal({ item, onClose, onDone }: { item: LolerChaseItem; onClose: () => void; onDone: (ref: string) => void }) {
  const [examDate, setExamDate] = useState("");
  const [reportRef, setReportRef] = useState("");
  const [inspector, setInspector] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const label = [item.name, item.eleNumber].filter(Boolean).join(" | ") || "this equipment";

  async function submit() {
    if (!examDate) {
      setErr("Enter the examination date shown on the certificate or report.");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await api.loler.createReport({
        assetId: item.assetId,
        examinationDate: examDate,
        ...(reportRef.trim() ? { reportRef: reportRef.trim() } : {}),
        ...(inspector.trim() ? { inspectorName: inspector.trim() } : {}),
        completeness: "full",
      });
      onDone(label);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message || e.code : "Could not record the examination. Please try again.");
      setBusy(false);
    }
  }

  return (
    <ModalShell ariaLabel="Record examination" onClose={() => !busy && onClose()} closeOnBackdrop width="max-w-md" panelClassName="p-6">
        <h2 className="mb-1 font-heading text-xl text-ele-strong">Record examination</h2>
        <p className="mb-4 text-sm text-ele-body">
          {label}
          {item.siteName ? ` at ${item.siteName}` : ""}. The next due date rolls six months from the
          examination date on the certificate, never from when the paperwork arrived.
        </p>
        <label className="mb-1 block text-sm text-ele-body" htmlFor="chase-exam-date">
          Examination date (from the certificate)
        </label>
        <input
          id="chase-exam-date"
          type="date"
          value={examDate}
          onChange={(e) => setExamDate(e.target.value)}
          className="mb-3 w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm outline-none focus:border-ele-green"
        />
        <label className="mb-1 block text-sm text-ele-body" htmlFor="chase-exam-ref">
          Report reference (optional)
        </label>
        <input
          id="chase-exam-ref"
          value={reportRef}
          onChange={(e) => setReportRef(e.target.value)}
          className="mb-3 w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm outline-none focus:border-ele-green"
        />
        <label className="mb-1 block text-sm text-ele-body" htmlFor="chase-exam-inspector">
          Examining body (optional)
        </label>
        <input
          id="chase-exam-inspector"
          value={inspector}
          onChange={(e) => setInspector(e.target.value)}
          placeholder="e.g. the insurer or examining company"
          className="mb-3 w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm outline-none focus:border-ele-green"
        />
        <p className="mb-3 text-xs text-ele-body opacity-70">
          The certificate PDF can be attached afterwards from the LOLER reports page.
        </p>
        {err && (
          <p role="alert" className="mb-3 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
            {err}
          </p>
        )}
        <div className={`gap-2 ${MODAL_ACTIONS}`}>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className={`rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body disabled:opacity-50 ${MODAL_ACTION_BTN}`}
          >
            Cancel
          </button>
          <Button size="sm" onClick={submit} disabled={busy} className={MODAL_ACTION_BTN}>
            {busy ? "Recording..." : "Record examination"}
          </Button>
        </div>
    </ModalShell>
  );
}

// ---- The consolidated compliance health dashboard (Item 4) ------------------
function HealthDashboard() {
  const [data, setData] = useState<ComplianceDashboard | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let active = true;
    setState("loading");
    api.compliance
      .dashboard()
      .then((d) => {
        if (!active) return;
        setData(d);
        setUpdatedAt(new Date().toISOString());
        setState("ready");
      })
      .catch(() => {
        if (active) setState("error");
      });
    return () => {
      active = false;
    };
  }, [refreshKey]);

  const HEALTH_LABEL: Record<ComplianceHealthStatus, string> = {
    red: "Action needed",
    amber: "Attention soon",
    green: "All clear",
  };

  return (
    <section>
      <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-baseline gap-3">
          <h2 className="font-heading text-xl tracking-wide text-ele-blue">Compliance health</h2>
          {updatedAt && <span className="text-xs text-ele-body opacity-70">Updated {formatTime(updatedAt)}</span>}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setRefreshKey((k) => k + 1)}
            disabled={state === "loading"}
            className="max-sm:min-h-11 rounded-ele border border-ele-purple-accent px-4 py-1.5 font-menu text-xs tracking-wide text-ele-body transition hover:text-ele-strong disabled:opacity-50"
          >
            {state === "loading" ? "Refreshing..." : "Refresh"}
          </button>
        </div>
      </div>

      {state === "loading" && !data ? (
        <Loader shape="inline" />
      ) : state === "error" || !data ? (
        <p role="alert" className="rounded-ele border border-ele-purple-accent bg-ele-surface p-6 text-center text-ele-strong">
          Could not load the compliance dashboard. Please try again in a moment.
        </p>
      ) : (
        <>
          <div className={`mb-4 overflow-hidden rounded-ele border border-ele-purple-accent`}>
            <div className={`flex items-baseline justify-between px-4 py-3 ${BAND[data.health.status]}`}>
              <span className="font-menu text-xs uppercase tracking-widest">Health: {HEALTH_LABEL[data.health.status]}</span>
              <span className="font-heading text-3xl">{data.health.totalOpen}</span>
            </div>
            <p className="px-4 py-2 text-xs text-ele-body opacity-80">
              {data.health.totalOpen === 0
                ? "No open compliance items across LOLER, SAFed, certifications, competence, RIDDOR and modifications."
                : `${data.health.totalOpen} open compliance item${data.health.totalOpen === 1 ? "" : "s"} across all categories.`}
            </p>
          </div>

          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            <DashPanel
              label="LOLER overdue + due 90 days"
              count={data.loler.items.length}
              tone={data.loler.overdue > 0 ? "red" : data.loler.dueSoon > 0 ? "amber" : "green"}
              empty="No LOLER examinations overdue or due within 90 days."
            >
              {data.loler.items.map((it) => (
                <Row key={it.id} to={`/office/lifts/${it.assetId}`} primary={it.ele} secondary={`${it.siteName} · ${it.customerName}`} right={`${it.status === "overdue" ? "Overdue" : "Due"}: ${orNoDate(it.dateIso)}`} rightTone={it.status === "overdue" ? "red" : "amber"} />
              ))}
            </DashPanel>

            <DashPanel
              label="SAFed overdue + due 90 days"
              count={data.safed.items.length}
              tone={data.safed.overdue > 0 ? "red" : data.safed.dueSoon > 0 ? "amber" : "green"}
              empty="No SAFed tests overdue or due within 90 days."
            >
              {data.safed.items.map((it) => (
                <Row key={it.id} to={`/office/lifts/${it.assetId}#safed`} primary={`${it.ele} ${it.testType}`} secondary={`${it.siteName} · ${it.customerName}`} right={`${it.status === "overdue" ? "Overdue" : "Due"}: ${orNoDate(it.nextDueDate)}`} rightTone={it.status === "overdue" ? "red" : "amber"} />
              ))}
            </DashPanel>

            <DashPanel
              label="Certifications expired + expiring 60 days"
              count={data.certs.items.length}
              tone={data.certs.expired > 0 ? "red" : data.certs.expiring > 0 ? "amber" : "green"}
              empty="No engineer certifications expired or expiring within 60 days."
            >
              {data.certs.items.map((it, i) => (
                <Row key={`${it.engineerName}-${it.certType}-${i}`} to={`/office/personnel?tab=engineer&engineer=${encodeURIComponent(it.engineerName)}`} primary={it.certType} secondary={it.engineerName} right={`${it.status === "expired" ? "Expired" : "Expires"}: ${orNoDate(it.expiryDate)}`} rightTone={it.status === "expired" ? "red" : "amber"} />
              ))}
            </DashPanel>

            <DashPanel
              label="Competence reviews due"
              count={data.competence.items.length}
              tone={data.competence.overdue > 0 ? "red" : data.competence.dueSoon > 0 ? "amber" : "green"}
              empty="No competence reviews overdue or due within 30 days."
            >
              {data.competence.items.map((it) => (
                <Row key={it.recordId} to={`/office/personnel?tab=engineer&engineer=${encodeURIComponent(it.engineerName)}`} primary={it.competenceArea} secondary={it.engineerName} right={`${it.reviewStatus === "overdue" ? "Overdue" : "Due"}: ${orNoDate(it.reviewDate)}`} rightTone={it.reviewStatus === "overdue" ? "red" : "amber"} />
              ))}
            </DashPanel>

            <DashPanel
              label="Open RIDDOR advisory flags"
              count={data.riddor.items.length}
              tone={data.riddor.open > 0 ? "red" : "green"}
              empty="No open RIDDOR advisory flags."
              note="Guidance for review, not legal advice. Decide on the To fix list under Repairs."
            >
              {data.riddor.items.map((it) => (
                <Row key={it.id} primary={it.ele} secondary={it.matchedCriterion} right={<Link to="/office/defects?riddor=open" className="text-ele-blue hover:underline">Review</Link>} />
              ))}
            </DashPanel>

            <DashPanel
              label="Open modification re-exam flags"
              count={data.modification.items.length}
              tone={data.modification.open > 0 ? "amber" : "green"}
              empty="No open modification re-examination flags."
              note="A significant modification may require a fresh LOLER examination. Review on the project."
            >
              {data.modification.items.map((it) => (
                <Row key={it.id} primary={it.projectRef ?? "Project"} secondary={it.projectTitle ?? ""} right={<Link to={`/office/projects/${it.projectId}`} className="text-ele-blue hover:underline">Review</Link>} />
              ))}
            </DashPanel>

            <SubcontractorCertPanel />
            <AccidentCompliancePanel />
            <EnforcementCompliancePanel />
          </div>
          <SafedAttentionSection />
        </>
      )}
    </section>
  );
}

// ---- Accident/RIDDOR compliance panel (Session AOO) --------------------------------------
// A small, targeted summary of the accident register for this dashboard, NOT the register itself
// (that is /office/accidents): the count of open accidents, and the ones that need an office
// decision right now -- either the RIDDOR determination has not been made yet, or it has been made
// as reportable and the written report to the HSE has not been sent. Self-fetches from its own
// endpoint (GET /api/accidents/dashboard-summary), matching the SubcontractorCertPanel precedent
// beside it rather than folding into the six-category buildComplianceDashboard aggregation.
function AccidentCompliancePanel() {
  const [data, setData] = useState<AccidentDashboardSummary | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");

  useEffect(() => {
    let active = true;
    setState("loading");
    api.accidents
      .dashboardSummary()
      .then((d) => {
        if (!active) return;
        setData(d);
        setState("ready");
      })
      .catch(() => {
        if (active) setState("error");
      });
    return () => {
      active = false;
    };
  }, []);

  if (state === "error") {
    return (
      <div role="alert" className="overflow-hidden rounded-ele border border-ele-purple-accent bg-ele-surface p-4 text-center text-sm text-ele-strong">
        Could not load the accident register summary. Please try again in a moment.
      </div>
    );
  }
  if (state === "loading" || !data) {
    return <Loader shape="inline" className="overflow-hidden rounded-ele border border-ele-purple-accent p-4" />;
  }

  // Red only when a written report is overdue or due today (writtenReportCountdown's own "urgent"
  // reading, the same one the register itself uses); amber for everything else that needs a look,
  // including "unassessed" -- matching the accident register's own status convention,
  // where an unassessed record and an open record are both amber, never red on their own; green
  // when there is genuinely nothing outstanding.
  const anyUrgent = data.needsAttention.some((it) => it.reason === "report_due" && writtenReportCountdown(it.daysToWrittenReport)?.urgent);
  const tone: Tone = data.needsAttention.length === 0 ? "green" : anyUrgent ? "red" : "amber";
  const note = `${data.openCount} open accident${data.openCount === 1 ? "" : "s"} on the register. ${data.reportableLast3Years} RIDDOR reportable in the last 3 years (the CHAS Q95 figure).`;

  return (
    <DashPanel
      label="Accidents needing RIDDOR attention"
      count={data.needsAttention.length}
      tone={tone}
      empty="Nothing needs a RIDDOR decision: every accident on record has been assessed, and every reportable one has been reported to the HSE."
      note={note}
    >
      {data.needsAttention.map((it) => {
        const countdown = it.reason === "report_due" ? writtenReportCountdown(it.daysToWrittenReport) : null;
        const right = it.reason === "unassessed" ? "Not yet assessed" : (countdown?.text ?? "Written report due");
        // Matches the overall panel tone above: unassessed is amber (the accident register's own
        // status convention), red is reserved for a report that is actually overdue or due
        // today.
        return (
          <Row
            key={it.id}
            to={`/office/accidents/${it.id}`}
            primary={it.ref}
            secondary={EVENT_TYPE_LABEL[it.eventType] ?? it.eventType}
            right={right}
            rightTone={countdown?.urgent ? "red" : "amber"}
          />
        );
      })}
    </DashPanel>
  );
}

// ---- Enforcement notices/prosecutions compliance panel (Session AOO) ---------------------
// A single figure, matching the prompt's own scope for this entity: how many enforcement notices
// or prosecutions are currently open. There is no itemised list here (unlike the accident panel
// above) because the register is small and every one of them, open or not, belongs on
// /office/enforcement-actions -- a bare count answers what the dashboard needs to answer without
// duplicating that page's own list.
function EnforcementCompliancePanel() {
  const [data, setData] = useState<EnforcementActionDashboardSummary | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");

  useEffect(() => {
    let active = true;
    setState("loading");
    api.enforcementActions
      .dashboardSummary()
      .then((d) => {
        if (!active) return;
        setData(d);
        setState("ready");
      })
      .catch(() => {
        if (active) setState("error");
      });
    return () => {
      active = false;
    };
  }, []);

  if (state === "error") {
    return (
      <div role="alert" className="overflow-hidden rounded-ele border border-ele-purple-accent bg-ele-surface p-4 text-center text-sm text-ele-strong">
        Could not load the enforcement notices summary. Please try again in a moment.
      </div>
    );
  }
  if (state === "loading" || !data) {
    return <Loader shape="inline" className="overflow-hidden rounded-ele border border-ele-purple-accent p-4" />;
  }

  const tone: Tone = data.openCount > 0 ? "amber" : "green";
  return (
    <Link to="/office/enforcement-actions" className="block overflow-hidden rounded-ele border border-ele-purple-accent transition hover:border-ele-green">
      <div className={`flex items-baseline justify-between px-4 py-3 ${BAND[tone]}`}>
        <span className="font-menu text-xs uppercase tracking-widest">Open enforcement notices and prosecutions</span>
        <span className="font-heading text-3xl">{data.openCount}</span>
      </div>
      <p className="px-4 py-3 text-xs text-ele-body opacity-70">
        {data.openCount === 0
          ? "No open HSE or Local Authority enforcement notices or prosecutions."
          : "See the Enforcement notices and prosecutions register for details and remedial action."}
      </p>
    </Link>
  );
}

// ---- Subcontractor certificate expiry panel (Session AEL, Director decision D37) ------------
// Using a subcontractor whose insurance or competency certificate has lapsed is a direct
// liability risk, so this panel sits beside the lift-side health panels and surfaces every
// subcontractor certificate that is already expired or due to expire within 30 days, from
// GET /api/subcontractors/certificates/expiring. It self-fetches (a separate endpoint from the
// rest of the health dashboard) and renders through the same DashPanel/Row components used
// above, so it reads as one of the family rather than a bolted-on extra.
//
// HONESTY OF STATE: `items` holds ONLY expired-or-expiring rows, so an empty list on its own
// cannot prove "every certificate is valid": it looks identical whether nothing has ever
// lapsed or nothing has ever been recorded in the first place. A green all-clear on that
// ambiguity would be exactly the false-confidence failure this panel exists to prevent (the
// same Session AEH honesty-of-state principle the operational tiles already follow). The
// server now closes the gap with `trackedCount`: the number of dated certificates that exist
// AT ALL across non-archived subcontractors, independent of `items`. Green is reachable only
// when trackedCount proves the dataset is genuinely non-empty and clean; trackedCount === 0
// (nothing dated ever recorded) always reads as the neutral "not recorded" state, never green.
function SubcontractorCertPanel() {
  const [data, setData] = useState<{
    items: SubcontractorExpiringCertificate[];
    expiredCount: number;
    expiringCount: number;
    trackedCount: number;
    unverifiedCount?: number;
  } | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");

  useEffect(() => {
    let active = true;
    setState("loading");
    api.subcontractors
      .expiringCertificates()
      .then((d) => {
        if (!active) return;
        setData(d);
        setState("ready");
      })
      .catch(() => {
        if (active) setState("error");
      });
    return () => {
      active = false;
    };
  }, []);

  if (state === "error") {
    return (
      <div role="alert" className="overflow-hidden rounded-ele border border-ele-purple-accent bg-ele-surface p-4 text-center text-sm text-ele-strong">
        Could not load subcontractor certificates. Please try again in a moment.
      </div>
    );
  }
  if (state === "loading" || !data) {
    return <Loader shape="inline" className="overflow-hidden rounded-ele border border-ele-purple-accent p-4" />;
  }

  // Full four-way tone: see the HONESTY OF STATE note above the component. Green is reachable
  // only via trackedCount, which proves the dataset is not empty; a load error is handled above
  // and can never fall through to any of these tones.
  // Session AUR: an unverified document never reads as compliant, so the green all-clear needs every document on file to
  // have been approved by the office as well as in date. An absent count (an older server) is treated as unknown, never 0.
  const unverified = data.unverifiedCount ?? null;
  const allVerified = unverified === 0;
  const tone: Tone =
    data.expiredCount > 0 ? "red" : data.expiringCount > 0 ? "amber" : data.trackedCount > 0 && allVerified ? "green" : "neutral";
  const empty =
    data.trackedCount === 0
      ? "No subcontractor certificates recorded. Add insurance and competency details on each subcontractor's page."
      : allVerified
        ? "All subcontractor certificates valid."
        : unverified == null
          ? "No certificate expired or expiring in 30 days. Whether each has been verified could not be read."
          : `No certificate expired or expiring in 30 days. ${unverified === 1 ? "1 document is" : `${unverified} documents are`} not yet verified by the office, so none of them counts as compliant yet.`;

  return (
    <DashPanel label="Subcontractor certificates expired + expiring 30 days" count={data.items.length} tone={tone} empty={empty}>
      {data.items.map((it) => (
        <Row
          key={it.id}
          to={`/office/subcontractors/${it.subcontractorId}`}
          primary={`${it.subcontractorName} · ${it.typeLabel}`}
          secondary={it.name ?? ""}
          right={certExpiryTiming(it.status, it.daysUntilExpiry)}
          rightTone={it.status === "expired" ? "red" : "amber"}
        />
      ))}
    </DashPanel>
  );
}

// Plain-English expiry timing for a subcontractor certificate row: "Expired N days ago" (or
// "Expired today" at zero) for an expired row, "Expires in N days" (or "Expires today") for an
// expiring one, with the correct singular "1 day" either way.
function certExpiryTiming(status: SubCertStatus, daysUntilExpiry: number | null): string {
  const days = Math.abs(daysUntilExpiry ?? 0);
  const unit = days === 1 ? "day" : "days";
  if (status === "expired") return days === 0 ? "Expired today" : `Expired ${days} ${unit} ago`;
  return days === 0 ? "Expires today" : `Expires in ${days} ${unit}`;
}

function DashPanel({ label, count, tone, empty, note, children }: { label: string; count: number; tone: Tone; empty: string; note?: string; children: React.ReactNode }) {
  const items = Array.isArray(children) ? children : [children];
  const hasItems = count > 0;
  return (
    <div className="overflow-hidden rounded-ele border border-ele-purple-accent">
      <div className={`flex items-baseline justify-between px-4 py-3 ${BAND[tone]}`}>
        <span className="font-menu text-xs uppercase tracking-widest">{label}</span>
        <span className="font-heading text-3xl">{count}</span>
      </div>
      {note ? <p className="border-b border-ele-purple-accent px-4 py-2 text-[11px] text-ele-body opacity-80">{note}</p> : null}
      {!hasItems ? (
        <p className="px-4 py-3 text-xs text-ele-body opacity-70">{empty}</p>
      ) : (
        <ul className="max-h-64 divide-y divide-ele-purple-accent overflow-y-auto text-sm">{items}</ul>
      )}
    </div>
  );
}

// Everything links (Session AF): when `to` is given, the row's primary label is a link directly to
// where the office acts on it (the lift, the Personnel page's Engineer tab, the project, the filtered
// defects list). Without `to` it stays plain text.
function Row({ primary, secondary, right, rightTone, to }: { primary: string; secondary: string; right: React.ReactNode; rightTone?: "red" | "amber"; to?: string }) {
  const rightCls = rightTone === "red" ? "text-ele-danger" : rightTone === "amber" ? "text-amber-400" : "text-ele-body";
  return (
    <li className="px-4 py-2">
      <div className="flex items-baseline justify-between gap-2">
        {to ? (
          <Link to={to} className="font-heading text-ele-green hover:underline">{primary}</Link>
        ) : (
          <span className="font-heading text-ele-strong">{primary}</span>
        )}
        <span className={`shrink-0 text-xs ${rightCls}`}>{right}</span>
      </div>
      {secondary ? <div className="text-xs text-ele-body">{secondary}</div> : null}
    </li>
  );
}

// ---- The operational tiles (existing) ---------------------------------------
function OperationalTiles() {
  const [data, setData] = useState<ComplianceOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(false);
    api.compliance
      .overview()
      .then((d) => setData(d))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => load(), [load]);

  if (loading) return <Loader shape="inline" />;
  if (error || !data)
    return (
      <p role="alert" className="rounded-ele border border-ele-purple-accent bg-ele-surface p-6 text-center text-ele-strong">
        Could not load the operational overview. Please try again in a moment.
      </p>
    );

  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
      <SummaryTile tone="neutral" label="Total lifts" count={data.totalLifts} to="/office/lifts" />
      {/* Honesty of state (Session AEH, FIX-2 B4): the green celebration only when every lift is
          compliant; otherwise it is a neutral count, not an all-clear signal. */}
      <SummaryTile
        tone={data.totalLifts > 0 && data.fullyCompliant === data.totalLifts ? "green" : "neutral"}
        label="Fully compliant"
        count={data.fullyCompliant}
      />
      <TileCard tone="red" label="LOLER overdue" tile={data.lolerOverdue} dateLabel="Was due" linkFor={liftHref} />
      <TileCard tone="amber" label="LOLER due within 30 days" tile={data.lolerDueSoon} dateLabel="Due" linkFor={liftHref} />
      <TileCard tone="red" label="PPM overdue" tile={data.ppmOverdue} dateLabel="Was due" linkFor={liftHref} />
      <TileCard tone="amber" label="PPM due within 14 days" tile={data.ppmDueSoon} dateLabel="Due" linkFor={liftHref} />
      <TileCard tone="red" label="Open Category A defects" tile={data.defectsCatA} dateLabel="Raised" linkFor={() => "/office/defects?category=A&status=open"} />
      <TileCard tone="amber" label="Open Category B defects" tile={data.defectsCatB} dateLabel="Raised" linkFor={() => "/office/defects?category=B&status=open"} />
      <TileCard tone="amber" label="Contracts renewing within 90 days" tile={data.renewalsDue90} dateLabel="Renews" linkFor={() => "/office/contracts?filter=due-90-days"} />
    </div>
  );
}

// Everything links (Session AF): a summary tile is a link to its list when `to` is given (Total
// lifts -> the Lifts register). Fully compliant is a positive status, so it carries no link.
// Session AG Item 1: a zero-count tile is never a navigable link (an empty list confuses), so the
// link is dropped when count is 0 -- the tile stays visible so the Director knows the metric exists.
function SummaryTile({ tone, label, count, to }: { tone: Tone; label: string; count: number; to?: string }) {
  const inner = (
    <div className={`flex items-baseline justify-between px-4 py-3 ${BAND[tone]}`}>
      <span className="font-menu text-xs uppercase tracking-widest">{label}</span>
      <span className="font-heading text-3xl">{count}</span>
    </div>
  );
  if (to && count > 0) {
    return (
      <Link to={to} className="block overflow-hidden rounded-ele border border-ele-purple-accent transition hover:border-ele-green">
        {inner}
      </Link>
    );
  }
  return <div className="overflow-hidden rounded-ele border border-ele-purple-accent">{inner}</div>;
}

// Everything links (Session AF): each row links directly to where the office acts on it. `linkFor`
// builds the destination per tile: LOLER/PPM rows -> the lift detail (/office/lifts/:id, the item id
// is the asset id); defect tiles -> the Defects page filtered to that category and open status;
// renewals -> the Contracts list pre-filtered to those due within 90 days.
function TileCard({ tone, label, tile, dateLabel, linkFor }: { tone: Tone; label: string; tile: ComplianceTile; dateLabel: string; linkFor: (item: ComplianceTile["items"][number]) => string }) {
  // Honesty of state (Session AEH, FIX-2 B4): the alert tone (red/amber) shows only when the tile
  // actually has items; an empty tile reads green (all clear), never a fixed alarm colour.
  return (
    <div className="overflow-hidden rounded-ele border border-ele-purple-accent">
      <div className={`flex items-baseline justify-between px-4 py-3 ${BAND[tile.count > 0 ? tone : "green"]}`}>
        <span className="font-menu text-xs uppercase tracking-widest">{label}</span>
        <span className="font-heading text-3xl">{tile.count}</span>
      </div>
      {tile.count === 0 ? (
        <p className="px-4 py-3 text-xs text-ele-body opacity-70">No items.</p>
      ) : (
        <ul className="max-h-64 divide-y divide-ele-purple-accent overflow-y-auto text-sm">
          {tile.items.map((it) => (
            <li key={it.id} className="px-4 py-2">
              <div className="flex items-baseline justify-between gap-2">
                <Link to={linkFor(it)} className="font-heading text-ele-green hover:underline">
                  {it.label}
                </Link>
                <span className="shrink-0 text-xs text-ele-body">
                  {dateLabel}: {orNoDate(it.dateIso)}
                </span>
              </div>
              <div className="text-xs text-ele-body">
                {it.siteName}
                {it.customerName ? ` · ${it.customerName}` : ""}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
